'use client';

import { useTranslations } from "next-intl";
import cUSDTImg from '@/features/notifications/images/deposit-usdt.png';
import tUSDTImg from '@/features/notifications/images/withdraw-usdt.png';
import cINRImg from '@/features/notifications/images/deposit-inr.png';
import tINRImg from '@/features/notifications/images/withdraw-inr.png';
import cBRLImg from '@/features/notifications/images/deposit-brl.png';
import tBRLImg from '@/features/notifications/images/withdraw-brl.png';
import proxyImg from '@/features/notifications/images/affiliate.png';
import vipImg from '@/features/notifications/images/vip.png';
import cumulativeDepositImg from '@/features/notifications/images/cumulative-deposit.png';
import csImg from '@/features/notifications/images/cs.png';
import csVipImg from '@/features/user-center/images/icon/customer-colorfull-vip.png';
import Empty from './empty';
import moreImg from '@/features/notifications/images/arrow-right-blue.png';
import rummyImg from '@/features/notifications/images/rummy.png';
import personalGeneralImg from '@/features/notifications/images/personal-general.png';
import Image from 'next/image';
import { NotificationItem, NotificationBannerRes, setNotificationRead, NotificationReadStatus, getNotificationByPage, type NotificationListRes } from "@/features/notifications/services";
import { CurrencyType, NotificationType, NotificationEventType, NotificationEventStatus, MsgCardTitleColor } from "@/features/notifications/constant";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import { useEffect, useMemo } from "react";
import useIsLogin from "@/features/notifications/hooks/useIsLogin";
import { useContactCustomerService } from '@/components/contact-customer-service/use-contact-customer-service';
import MsgCard from './msg-card';
import { Spin } from '@/components/ui-feat/spin';
import { useNotificationJump } from '../hooks/useNotificationJump';
import { formatSecondToDate } from '@/utils/time';
import { useGetVipUrl } from "@/services/chat-vip-url";

interface PersonalListProps {
  decUnreadNumAction: (type: NotificationType) => void;
  closeDrawer: () => void;
  trackRead: (msgItem: NotificationItem) => void;
  hasReadAll: boolean;
  // 点击底部按钮跳转事件
  onJumpClick: (msgItem: NotificationItem, btnName: string) => void;
}

export default function PersonalList({ decUnreadNumAction, closeDrawer, trackRead, hasReadAll, onJumpClick }: PersonalListProps) {
  const t = useTranslations('notifications'); // 多语言模块  
  const tCommon = useTranslations('common');
  const { isLogin } = useIsLogin();

  const { handleContact, isPending } = useContactCustomerService({ source: 'Inbox Message' });

  const { data: vipCustomerData } = useGetVipUrl();

  const { jumpByEventType } = useNotificationJump((detail: NotificationItem | NotificationBannerRes) => {
    console.log('跳转完成', detail);
    doSomethingAfterJump(detail);
  });

  function getImg(item: NotificationItem) {
    switch (item.event_type) {
      case NotificationEventType.Recharge:
        switch (item.currency) {
          case CurrencyType.INR:
            return cINRImg;
          case CurrencyType.BRL:
            return cBRLImg;
          case CurrencyType.USDT:
            return cUSDTImg;
          default:
            return cINRImg;
        }
      case NotificationEventType.Withdraw:
        switch (item.currency) {
          case CurrencyType.INR:
            return tINRImg;
          case CurrencyType.BRL:
            return tBRLImg;
          case CurrencyType.USDT:
            return tUSDTImg;
          default:
            return tINRImg;
        }
      case NotificationEventType.Compensation:
        switch (item.currency) {
          case CurrencyType.INR:
            return cINRImg;
          case CurrencyType.BRL:
            return cBRLImg;
          case CurrencyType.USDT:
            return cUSDTImg;
          default:
            return cINRImg;
        }
      case NotificationEventType.Proxy:
        return proxyImg;
      case NotificationEventType.VIP:
        return vipImg;
      case NotificationEventType.Rummy:
        return rummyImg;
      case NotificationEventType.CumulativeDeposit:
        return cumulativeDepositImg;
      case NotificationEventType.TransferPackage:
        return personalGeneralImg;
      default:
        return vipImg;
    }
  }
  function fetchData(page: number, pageSize: number): Promise<NotificationListRes> {
    // 只在登录时才请求数据
    if (!isLogin) {
      return Promise.resolve({ list: [], total: 0 });
    }
    return getNotificationByPage({
      msg_type: NotificationType.Personal,
      page: page,
      size: pageSize
    });
  }

  const { items, setItems, containerRef, loading, hasMore } = useInfiniteScroll(fetchData, 10);

  async function setReadMsg(item: NotificationItem) {
    // 只在登录时才允许设置已读
    if (!isLogin) return null;
    const res = await setNotificationRead({ id: item.id });
    return res;
  }
  async function readMsg(item: NotificationItem) {
    // 埋点上报
    trackRead(item);
    const res = await setReadMsg(item);
    if (res?.status === 1) {
      updateMsgData({
        ...item,
        is_read: NotificationReadStatus.READED,
      });
    }
  }
  async function toggleShowAllMsg(item: NotificationItem, isShowAll: boolean) {
    const newMsg = {
      ...item,
      isShowAll: isShowAll
    };
    if (item.is_read === NotificationReadStatus.UNREAD && isShowAll) {
      // 埋点上报
      // trackRead(item, t('showAll'));
      trackRead(item);
      const res = await setReadMsg(item);
      if (res?.status === 1) {
        newMsg.is_read = NotificationReadStatus.READED;
      }
    }

    updateMsgData(newMsg);
  }
  function updateMsgData(item: NotificationItem) {
    setItems(
      items.map((msgItem) => {
        if (msgItem.id === item.id) {
          // 更新父组件未读数量
          if (msgItem.is_read === NotificationReadStatus.UNREAD && item.is_read === NotificationReadStatus.READED) {
            decUnreadNumAction(NotificationType.Personal);
          }
          return item;
        }
        return msgItem;
      })
    );
  }

  function handleJump(notifyDetail: NotificationItem, btnName: string) {
    const { is_read } = notifyDetail || {};
    onJumpClick(notifyDetail, btnName);
    jumpByEventType(notifyDetail);

    if (is_read === NotificationReadStatus.UNREAD) {
      readMsg(notifyDetail);
    }
  }

  // 处理跳转后的逻辑
  function doSomethingAfterJump(notifyDetail: NotificationItem | NotificationBannerRes) {
    const { event_type } = notifyDetail || {};

    switch (event_type) {
      case NotificationEventType.Proxy:
        closeDrawer();
        break;
      case NotificationEventType.VIP:
        closeDrawer();
        break;
      case NotificationEventType.Rummy:
        closeDrawer();
        break;
      case NotificationEventType.CumulativeDeposit:
        closeDrawer();
        break;
      default:
        break;
    }
  }


  // 跳转至客服页面
  function jumpToCS(detail: NotificationItem) {
    console.log('jumpToCS', isPending);
    // if (!isPending) return;

    const type = detail.event_type === NotificationEventType.Recharge ? '1' : '2';
    const orderId = detail.order_id;
    handleContact(type, orderId);
  }

  // 父组件已读全部 则将所有消息设置为已读状态
  useEffect(() => {
    if (hasReadAll) {
      setItems(preList => preList.map((item) => {
        // 只有未读消息才设置为已读
        if (item.is_read === NotificationReadStatus.UNREAD) {
          // 埋点上报
          // trackRead(item, t('markAllAsRead'));
        }
        return ({ ...item, is_read: NotificationReadStatus.READED });
      }));
    }
  }, [hasReadAll, setItems]);

  // 查看详情按钮
  const detailButton = (detail: NotificationItem) => {
    return (
      <div className="flex items-center text-accent-blue text-[12px] font-medium mr-[15px] pressable active:scale-90" onClick={() => handleJump(detail, 'Details')}>
        <span className="max-w-[80px] text-ellipsis overflow-hidden">{t('viewDetails')}</span>
        <Image src={moreImg} alt="" className="w-[6px] h-[10px] ml-[5px]" />
      </div>
    );
  };

  const customerImg = useMemo(() => {
    return !!vipCustomerData?.enable ? csVipImg : csImg;
  }, [vipCustomerData]);

  // 联系客服按钮
  const contactButton = (detail: NotificationItem) => {
    return (
      <div className="flex items-center text-accent-blue text-[12px] font-medium mr-[15px] pressable active:scale-90" onClick={() => jumpToCS(detail)}>
        <Image src={customerImg} alt="" className="w-[18px] h-[15px] mr-[3px]" />
        <span className="max-w-[80px] text-ellipsis overflow-hidden">{t('contactSupport')}</span>
        <Image src={moreImg} alt="" className="w-[6px] h-[10px] ml-[5px]" />
      </div>
    );
  };

  // 奖励中心按钮
  const rewardButton = (detail: NotificationItem) => {
    return (
      <div className="flex items-center text-accent-blue text-[12px] font-medium mr-[15px] pressable active:scale-90" onClick={() => handleJump(detail, 'Rewards centre')}>
        <span className="">{t('rewardCenter')}</span>
        <Image src={moreImg} alt="" className="w-[6px] h-[10px] ml-[5px]" />
      </div>
    );
  };

  // join按钮（rummy和累充用到）
  const joinButton = (detail: NotificationItem) => {
    return (
      <div className="flex items-center text-accent-blue text-[12px] font-medium mr-[15px] pressable active:scale-90" onClick={() => handleJump(detail, 'Join')}>
        <span className="max-w-[80px] text-ellipsis overflow-hidden">{t('join')}</span>
        <Image src={moreImg} alt="" className="w-[6px] h-[10px] ml-[5px]" />
      </div>
    );
  };

  const buttonGroup = (detail: NotificationItem) => {
    const { event_type, event_status, event_sub_type } = detail || {};

    // 充值、提现、代理都显示【查看详情】按钮，充值或提现失败时多显示一个【联系客服】按钮

    // 需要显示查看详情按钮的事件类型
    const showDetailButtonEventTypes = [
      NotificationEventType.Recharge,
      NotificationEventType.Withdraw,
    ];

    if (showDetailButtonEventTypes.includes(event_type)) {
      return (
        <>
          {detailButton(detail)}
          {((event_type === NotificationEventType.Recharge || event_type === NotificationEventType.Withdraw) && event_status === NotificationEventStatus.Fail) && contactButton(detail)}
        </>
      );
    } else if (event_type === NotificationEventType.Proxy) {
      // 代理事件中受邀和邀请事件不需要跳转，隐藏【查看详情】按钮
      const showDetailBtnAgentSubTypes = [1, 4, 5];
      return (
        <>
          {showDetailBtnAgentSubTypes.includes(event_sub_type) && detailButton(detail)}
        </>
      );
    } else if (event_type === NotificationEventType.VIP) {
      // VIP显示奖励中心按钮
      return (
        <>
          {rewardButton(detail)}
        </>
      );
    } else if (event_type === NotificationEventType.Rummy) {
      // rummy显示跳转join按钮
      return (
        <>
          {joinButton(detail)}
        </>
      );
    } else if (event_type === NotificationEventType.CumulativeDeposit) {
      // 累充显示跳转join按钮
      return (
        <>
          {joinButton(detail)}
        </>
      );
    }
  };

  const getMsgCardTitleColor = (detail: NotificationItem): string => {
    const { event_type, event_status } = detail || {};
    // 有可能不用默认标题颜色的事件类型
    const types = [
      NotificationEventType.Recharge,
      NotificationEventType.Withdraw
    ];
    if (types.includes(event_type)) {
      if (event_status === NotificationEventStatus.Success) {
        return MsgCardTitleColor.Success;
      }
      if (event_status === NotificationEventStatus.Fail) {
        return MsgCardTitleColor.Fail;
      }
      return MsgCardTitleColor.Default;
    }
    return MsgCardTitleColor.Default;
  };

  return (
    <div className="overflow-y-auto h-[calc(80vh-110px)] mt-[15px] no-scrollbar box-border px-[12px]" ref={containerRef}>
      {items.length === 0 && loading && <Spin className='w-full h-full' />}
      {items.length === 0 && !loading && <Empty showText />}
      {
        items.length > 0 && items.map((detail, index) => {
          return (
            <MsgCard
              className="mb-[20px]"
              key={`${detail.id}-${index}`}
              msgId={detail.id}
              icon={getImg(detail)}
              title={detail.title}
              content={detail.content}
              time={formatSecondToDate(detail.create_at, 'DD/MM HH:mm')}
              isRead={detail.is_read === NotificationReadStatus.READED}
              msgDetail={detail}
              titleClassName={getMsgCardTitleColor(detail)}
              onReadClick={readMsg}
              onExpandClick={toggleShowAllMsg}
              btnGroupSlot={buttonGroup(detail)}
            />
          );
        })
      }
      {loading && items.length > 0 && (
        <div className="flex items-center justify-center py-4">
          <div className="text-tertiary text-sm">{tCommon('loading')}</div>
        </div>
      )}
      {!hasMore && items.length > 0 && (
        <div className="flex items-center justify-center py-4">
          <div className="text-tertiary text-xs">{tCommon('noMoreData')}</div>
        </div>
      )}
    </div>
  );
};
